import {
    type ButtonHTMLAttributes,
    type ComponentPropsWithoutRef,
    type ReactElement,
    forwardRef,
} from 'react';

import { cn } from '@componentLibrary/utils';

/** Same values as the HTML `type` attribute on `<button>` (`button` | `submit` | `reset`). */
export type IconButtonHtmlType = ButtonHTMLAttributes<HTMLButtonElement>['type'];

export type IconButtonProps = Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
    children: ReactElement;
};

// This is a really basic implementation - update this incrementally based on use case
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            children,
            className,
            onClick,
            disabled = false,
            type: htmlButtonType = 'button',
            ...rest
        },
        ref,
    ) => {
        return (
            <button
                ref={ref}
                // eslint-disable-next-line react/button-has-type -- mirrors native `<button type>` from props (default `button`)
                type={htmlButtonType}
                {...rest}
                className={cn(
                    'flex bg-transparent outline-none border-none rounded-full cursor-pointer p-2 hover:bg-[#e9edf3]',
                    disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                    className,
                )}
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
            >
                {children}
            </button>
        );
    },
);

IconButton.displayName = 'IconButton';

export default IconButton;
